import { useLocale } from "@/i18n/LocaleProvider";
import { LocalizedText } from "@/i18n/LocalizedText";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Card, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import {
  ArrowRight,
  Bug,
  CheckCircle,
  FileText,
  Loader2,
  Mail,
  MessageCircleQuestionMark,
  Settings,
  TriangleAlert,
} from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { remoteConfig } from "../firebase";
import { fetchAndActivate, getValue } from "firebase/remote-config";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { supportAPI } from "@/services/api";

type FormData = {
  name: string;
  email: string;
  message: string;
  reason: string;
};

type SupportReason = "general-question" | "bug-report" | "feature-request";

const SupportPage = () => {
  const { t } = useLocale();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>();

  const [tab, setTab] = useState<SupportReason>("general-question");

  const supportRequestMutation = useMutation({
    mutationFn: (data: FormData) =>
      supportAPI.sendRequest({
        name: data.name,
        email: data.email,
        message: data.message,
        reason: tab,
      }),
    onSuccess: () => {
      toast.success(t("legacy.support.request.sent.13u31nd"));
      reset();
      setTab("general-question");
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.message ||
          "Failed to send support request. Please try again.",
      );
    },
  });

  const onSubmit = (data: FormData) => {
    supportRequestMutation.mutate(data);
  };

  const { data: faqs, isLoading } = useQuery({
    queryKey: ["remoteConfig", "FAQ"],
    queryFn: async () => {
      await fetchAndActivate(remoteConfig);
      const rawValue = getValue(remoteConfig, "FAQ").asString();
      return JSON.parse(rawValue || "[]");
    },
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  return (
    <div className="w-full mx-auto space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="flex items-start space-x-4 p-6">
          <div className="bg-gray-100 rounded-full p-3">
            <Mail />
          </div>
          <div>
            <CardTitle className="text-lg font-semibold mb-3">
              <LocalizedText id="legacy.contact.info.sstaej" />
            </CardTitle>
            <CardDescription className="flex flex-col gap-3">
              <div>
                <LocalizedText id="legacy.need.direct.help.email.us.we.usually.reply.fd8o9" />
              </div>
              <div className="text-foreground font-medium">help@nobori.ai</div>
            </CardDescription>
          </div>
        </Card>
        <a
          href="https://docs.nobori.ai/"
          target="_blank"
          rel="noopener noreferrer"
        >
          <Card className="flex items-start space-x-4 p-6">
            <div className="flex justify-between w-full">
              <div className="bg-gray-100 rounded-full p-3">
                <FileText />
              </div>
              <ArrowRight className="text-[#FF5E00]"></ArrowRight>
            </div>

            <div>
              <CardTitle className="text-lg font-semibold mb-3">
                <LocalizedText id="legacy.documentation.1m8aczb" />
              </CardTitle>
              <CardDescription className="flex flex-col gap-3">
                <div>
                  <LocalizedText id="legacy.dive.deeper.into.setup.integrations.and.analytics.workflow.11e8sm8" />
                </div>
                <div className="text-[#FF5E00] font-semibold">
                  <LocalizedText id="legacy.view.full.documentation.1hwf7ft" />
                </div>
              </CardDescription>
            </div>
          </Card>
        </a>
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
        <Card className="p-6 gap-4">
          <CardTitle>
            <LocalizedText id="legacy.contact.our.team.1n26dyo" />
          </CardTitle>
          <CardDescription>
            <LocalizedText id="legacy.have.a.question.found.a.bug.or.want.1g3vkr3" />
          </CardDescription>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <Tabs
              value={tab}
              onValueChange={(value) => setTab(value as SupportReason)}
            >
              <TabsList className="">
                <TabsTrigger
                  className="cursor-pointer"
                  value="general-question"
                >
                  <MessageCircleQuestionMark />{" "}
                  <LocalizedText id="legacy.general.question.1k38lst" />
                </TabsTrigger>
                <TabsTrigger className="cursor-pointer" value="bug-report">
                  <Bug /> <LocalizedText id="legacy.bug.report.1d2gad7" />
                </TabsTrigger>
                <TabsTrigger className="cursor-pointer" value="feature-request">
                  <Settings />{" "}
                  <LocalizedText id="legacy.feature.request.1x201c0" />
                </TabsTrigger>
              </TabsList>
            </Tabs>

            {supportRequestMutation.isSuccess && (
              <div
                role="status"
                className="flex items-start gap-2 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800"
              >
                <CheckCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>
                  <LocalizedText id="legacy.your.request.was.sent.we.usually.reply.within.1svkt3" />
                </span>
              </div>
            )}

            {supportRequestMutation.isError && (
              <div
                role="alert"
                className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800"
              >
                <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
                <span>
                  We could not send the request. You can still email
                  help@nobori.ai.
                </span>
              </div>
            )}

            <div className="flex flex-col gap-2">
              <Label htmlFor="name">
                <LocalizedText id="legacy.full.name.bcz7ox" />
              </Label>
              <Input
                id="name"
                placeholder={t("legacy.enter.your.full.name.1mqiwjs")}
                {...register("name", { required: "Name is required" })}
              />
              {errors.name && (
                <p className="text-red-600 text-sm mt-1">
                  {errors.name.message}
                </p>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="email">
                <LocalizedText id="legacy.your.email.11y1394" />
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="namename@company.com"
                {...register("email", {
                  required: "Email is required",
                  pattern: {
                    value: /^\S+@\S+$/i,
                    message: "Invalid email address",
                  },
                })}
              />
              {errors.email && (
                <p className="text-red-600 text-sm mt-1">
                  {errors.email.message}
                </p>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="message">
                <LocalizedText id="legacy.message.ol2umm" />
              </Label>
              <textarea
                id="message"
                autoComplete="off"
                placeholder={t("legacy.type.your.message.here.1sb48sz")}
                {...register("message", { required: "Message is required" })}
                className="border rounded-md w-full py-4 px-4 text-sm"
              />
              {errors.message && (
                <p className="text-red-600 text-sm mt-1">
                  {errors.message.message}
                </p>
              )}
            </div>

            <Button
              type="submit"
              disabled={supportRequestMutation.isPending}
              className="w-fit text-normal hover:bg-[#ff8138] hover:text-white text-white cursor-pointer bg-[#FF5E00]"
            >
              {supportRequestMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <LocalizedText id="legacy.sending.155my8n" />
                </>
              ) : (
                "Send request"
              )}
            </Button>
          </form>
        </Card>

        <Card className="p-6">
          <CardTitle>
            <LocalizedText id="legacy.frequently.asked.questions.eoopun" />
          </CardTitle>
          <CardDescription>
            <LocalizedText id="legacy.quick.answers.about.plans.visibility.tracking.and.al.7sfoxp" />
          </CardDescription>
          {isLoading ? (
            <div>
              <LocalizedText id="legacy.loading.1ooa6x9" />
            </div>
          ) : (
            <Accordion type="single" collapsible className=" space-y-2">
              {!faqs.length ? (
                <div>
                  <LocalizedText id="legacy.no.data.1dp9hn8" />
                </div>
              ) : (
                faqs.map(
                  (faq: { title: string; data: string }, index: number) => {
                    return (
                      <AccordionItem
                        key={index}
                        value={String(index)}
                        className="border-none rounded-md bg-[#F5F5F5] p-3"
                      >
                        <AccordionTrigger className="p-0 hover:no-underline cursor-pointer">
                          {faq.title}
                        </AccordionTrigger>
                        <AccordionContent className="pt-4">
                          {faq.data}
                        </AccordionContent>
                      </AccordionItem>
                    );
                  },
                )
              )}
            </Accordion>
          )}
        </Card>
      </div>
    </div>
  );
};

export default SupportPage;
